import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, User, LogIn, Building, CreditCard, Layers, Users, Wallet, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { cn } from "@/lib/utils";
import {
  AccountManagerExpanded,
  CostCenterManagerExpanded,
  PaymentMethodManagerExpanded,
  SupplierManagerExpanded,
} from "@/components/managers/ExpandedManagers";
import { ChartOfAccountsUnifiedManager } from "@/components/managers/ChartOfAccountsUnifiedManager";
import { useOrganization } from "@/hooks/useOrganization";
import TeamSettingsContent from "@/components/team/TeamSettingsContent";
import { useSearchParams } from "react-router-dom";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

export default function Cadastros() {
  const { user, loading } = useAuth();
  const { isOwner } = useOrganization();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const [activeSection, setActiveSection] = React.useState(tabFromUrl || "plano-contas");
  
  React.useEffect(() => {
    document.title = "Cadastros | Controle Financeiro Pessoal";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Gerencie planos de contas e contas financeiras.');
  }, []);

  const navigationItems = [
    { id: "plano-contas", label: "Plano de Contas", icon: Layers },
    { id: "centros-custo", label: "Centros de custo", icon: Building },
    { id: "contas", label: "Contas", icon: CreditCard },
    { id: "fornecedores", label: "Fornecedores", icon: Users },
    { id: "metodos-pagamento", label: "Formas de Pagamento", icon: Wallet },
    ...(isOwner ? [{ id: "membros", label: "Membros", icon: UsersRound }] : []),
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "plano-contas":
        return <ChartOfAccountsUnifiedManager />;
      case "centros-custo":
        return <CostCenterManagerExpanded />;
      case "contas":
        return <AccountManagerExpanded />;
      case "fornecedores":
        return <SupplierManagerExpanded />;
      case "metodos-pagamento":
        return <PaymentMethodManagerExpanded />;
      case "membros":
        return <TeamSettingsContent />;
      default:
        return <ChartOfAccountsUnifiedManager />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="page-bleed py-8 flex justify-center">
          <div>Carregando...</div>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="page-bleed py-8">
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Login Necessário
              </CardTitle>
              <CardDescription>
                Você precisa fazer login para gerenciar seus cadastros.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Acesso Restrito</AlertTitle>
                <AlertDescription>
                  Para salvar e sincronizar seus fornecedores, categorias e demais dados, 
                  faça login em sua conta.
                </AlertDescription>
              </Alert>
              <div className="mt-4">
                <Button onClick={() => window.location.href = '/auth'} className="w-full">
                  <LogIn className="h-4 w-4 mr-2" />
                  Fazer Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="page-bleed py-4 md:py-8">
        {/* Mobile/Tablet: Horizontal scrollable tabs */}
        <div className="lg:hidden mb-4">
          <ScrollArea className="w-full">
            <div className="flex gap-2 pb-2">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors border",
                      activeSection === item.id
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-border hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    {item.label}
                  </button>
                );
              })}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>

        <div className="flex gap-6 h-full">
          {/* Desktop Sidebar */}
          <div className="w-64 flex-shrink-0 hidden lg:block">
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Cadastros</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <nav className="space-y-1">
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveSection(item.id)}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium transition-colors",
                          "hover:bg-muted rounded-none",
                          activeSection === item.id
                            ? "bg-primary text-primary-foreground hover:bg-primary/90"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </button>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {renderContent()}
          </div>
        </div>
      </main>
    </div>
  );
}
